// Manifest sync engine. Polls /player/manifest with If-None-Match,
// dispatches events to whoever wires the callbacks (typically Player.tsx).
//
// Critical invariant: this engine does NOT persist `lastManifestHash` or
// `lastGoodManifest`. That responsibility belongs to the hydration step,
// which only marks a manifest as "good" after every referenced blob is
// successfully cached. Until then, the next sync will use the prior hash
// and any 200 response means "try to hydrate again".

import { fetchManifest, UnauthorizedError } from './api';
import { MANIFEST_SYNC_INTERVAL_MS } from './config';
import type { Manifest } from './types';

export interface SyncEngineEvents {
  onManifestUpdate?: (manifest: Manifest, etag: string) => void;
  onNotModified?: () => void;
  onUnauthorized?: () => void;
  onError?: (err: Error) => void;
}

export interface SyncEngine {
  /** Start polling. Idempotent. */
  start: () => void;
  /** Stop polling. In-flight requests will not fire callbacks after this. */
  stop: () => void;
  /** Trigger an immediate sync outside the polling interval. */
  syncNow: () => Promise<void>;
}

export function createSyncEngine(
  token: string,
  getLastHash: () => string | null,
  events: SyncEngineEvents,
): SyncEngine {
  let intervalId: number | null = null;
  let stopped = false;

  async function syncNow(): Promise<void> {
    if (stopped) return;
    const hash = getLastHash();
    try {
      const result = await fetchManifest(token, hash);
      if (stopped) return;
      if (result.kind === 'NOT_MODIFIED') {
        events.onNotModified?.();
      } else {
        events.onManifestUpdate?.(result.manifest, result.etag);
      }
    } catch (err) {
      if (stopped) return;
      if (err instanceof UnauthorizedError) {
        events.onUnauthorized?.();
      } else if (err instanceof Error) {
        events.onError?.(err);
      } else {
        events.onError?.(new Error(String(err)));
      }
    }
  }

  function start(): void {
    if (intervalId !== null) return;
    stopped = false;
    // Immediate first sync, then poll on interval.
    void syncNow();
    intervalId = window.setInterval(() => {
      void syncNow();
    }, MANIFEST_SYNC_INTERVAL_MS);
  }

  function stop(): void {
    stopped = true;
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  return { start, stop, syncNow };
}
